import { medicalAidDisplay } from 'src/app/core/utils/scheme-code';
import { formatDate, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import autoTable from 'jspdf-autotable';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { withPdfPages } from '../pdf-pages/with-pdf-pages';
import { environment } from './../../../environments/environment';
import { AgeFromDatePipe } from './../../shared/pipes/age-from-date.pipe';
import { GroupService } from './group.service';
import { UserService } from './user.service';
import { FileDownloadService } from './file-download.service';

@Injectable({
  providedIn: 'root'
})
export class MedicationService {
  group!: any;
  private medicationsCache: any[] | null = null;
  private preferredMedication: any[] = [];

  private apiUrl = 'api';

  private bundleSubject = new BehaviorSubject<any>(null);
  bundles$ = this.bundleSubject.asObservable();



  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object,
    private userService: UserService,
    private groupService: GroupService,
    private ageFromDate: AgeFromDatePipe,
    private fileDownloadService: FileDownloadService
  ) {

    this.userService.userProfile$.pipe(
      switchMap(currentUser => {
        if (currentUser) {
          this.preferredMedication = currentUser.preferredMedication || [];
          return this.getMedicationBundlesByUser(currentUser.id);
        }
        this.preferredMedication = [];
        return EMPTY;
      })
    ).subscribe();

  }

  getMedicationBundlesByUser(userId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/medications/bundles/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((bundles: any) => {
        this.bundleSubject.next(bundles);
      })
    );
  }

  updateMedicationBundles(bundles: any[]): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/medications/bundles`, { bundles }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getMedicationBundlesByUser(this.userService.user.id)),
    );
  }

  getMedicationsBySearchTerm(term: string): Observable<any[]> {
    if (term?.length <= 3 || typeof term !== 'string') {
      return of([]);
    }
    const token = localStorage.getItem('token');
    term = encodeURIComponent(term);
    return this.http.get<any[]>(`${this.apiUrl}/medications/search/?term=${term}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).
      pipe(
        map((results => {
          if (this.preferredMedication.length > 0) {
            const filtredPreferred = this.preferredMedication.filter((med: any) => med.name.toLowerCase().includes(term.toLowerCase()));
            return [...filtredPreferred, ...results.filter((med: any) => !filtredPreferred.some((prefMed: any) => prefMed.id === med.id))];

          }
          return results;
        }))
      );
  }

  verifySigningDoctor(credentials: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/user/verify-doctor`, credentials, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  checkIndications(medication: any, allergies: any, conditions: any, userId: string, groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/medications/indications`, { medication, allergies, conditions, userId, groupId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadPhysicianDetails(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/profile/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  emailPrescription(patient: any, groupId: string, prescriptions: any[], allowSigning: boolean, confirmSubstitutions: boolean, physicianId: string, fileName: string, email: string, ccEmail: string, triggerDownload: boolean = false): Observable<any> {

    const token = localStorage.getItem('token');

    // Generate the PDF and handle the async operation with from()
    return from(this.createPatientPrescriptionPdf(patient, prescriptions, allowSigning, confirmSubstitutions, physicianId, fileName).then(async (pdfBytes) => {
      // Create FormData to upload the PDF
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', blob, fileName);
      formData.append('patientId', patient.id);
      formData.append('patientName', patient.name);
      formData.append('email', email);
      formData.append('ccEmail', ccEmail);
      formData.append('groupId', groupId);

      if (triggerDownload) {
        const patientName = `${patient.name}_${patient.surname}`;
        await this.fileDownloadService.download(blob, `${patientName}_${moment().valueOf()}_prescription.pdf`);
      }

      await withPdfPages(formData);

      return this.http.post(`${this.apiUrl}/medications/email`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      }).toPromise(); // Convert the Observable to a Promise to resolve it within from()
    }));
  }

  async emailExistingPrescription(patient: any, groupId: string, details: any, fileName: string, email: string, ccEmail: string, allowSigning?: boolean) {
    const token = localStorage.getItem('token');
    //const prescription: any = await this.savePatientPrescription(details.physicianDetails, details.groupDetails, details.patientDetails, details.medication, details.confirmSubstitutions).toPromise();
    const pdfBytes = await this.downloadPdf(details, allowSigning!);

    const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    /*  const url = URL.createObjectURL(blob);
     window.open(url); */

    // Create FormData to upload the PDF
    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('patientId', patient.id);
    formData.append('patientName', patient.name);
    formData.append('email', email);
    formData.append('ccEmail', ccEmail);
    formData.append('groupId', groupId);
    await withPdfPages(formData);

    // Upload the PDF to the server and trigger email sending
    return this.http.post(`${this.apiUrl}/medications/email`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    }).toPromise(); // Convert the Observable to a Promise to resolve it within from()
  }

  /*  getMedicationsBySearchTerm(term: string): Observable<any[]> {
     if (this.medicationsCache) {
       return of(this.filterMedications(term, this.medicationsCache));
     } else {
       return this.http.get<any[]>('/local/api/medications').pipe(
         tap(data => this.medicationsCache = data),
         map(data => this.filterMedications(term, data))
       );
     }
   } */

  /*  private filterMedications(term: string, medications: any[]): any[] {
     const lowerTerm = term.toLowerCase();
     return medications.filter(med =>
       med.name.toLowerCase().includes(lowerTerm) ||
       med.activeIngredients.some((ing: any) => ing.name.toLowerCase().includes(lowerTerm)) ||
       med.nappiCode.toLowerCase().includes(lowerTerm)
     );
   } */

  getSimilarMedicatsions(nappiCode: string): Observable<any[]> {
    if (nappiCode?.length <= 3 || typeof nappiCode !== 'string') {
      return of([]);
    }
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/medications/${nappiCode}/related`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  savePatientPrescription(physicianDetails: any, groupDetails: any, patientDetails: any, medication: any, confirmSubstitutions: boolean, allowSigning: boolean) {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/prescriptions/patient/${patientDetails.id}`, { physicianDetails, groupDetails, patientDetails, medication, confirmSubstitutions, allowSigning }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  verifyPrescription(id: string) {
    return this.http.get(`${this.apiUrl}/prescriptions/verify/${id}`);
  }

  async createPatientPrescriptionPdf(patient: any, prescriptions: any[], allowSigning: boolean, confirmSubstitutions: boolean, physicianId: string, fileName: string): Promise<Uint8Array> {
    const physician = await this.loadPhysicianDetails(physicianId).toPromise();
    const content = this.generatePrescriptionContent(patient, prescriptions);
    const group = this.groupService.group;
    const prescription: any = await this.savePatientPrescription(physician.user, group, content.patient, prescriptions, confirmSubstitutions, allowSigning).toPromise();
    const pdfBytes = await this.downloadPdf(prescription!.data, allowSigning);
    return pdfBytes;
  }

  // Returns the rendered bytes only. Naming the download is the caller's job — an ignored fileName
  // parameter here previously made call sites look like they had named the file when they had not.
  async createVerifiedPrescriptionPdf(details: any, allowSigning?: boolean): Promise<Uint8Array> {
    //const prescription: any = await this.savePatientPrescription(details.physicianDetails, details.groupDetails, details.patientDetails, details.medication, details.confirmSubstitutions).toPromise();
    const pdfBytes = await this.downloadPdf(details, allowSigning!);
    return pdfBytes;
  }

  private generatePrescriptionContent(patient: any, prescriptions: any[]): any {
    return {
      patient: {
        ...patient,
        name: `${patient.title ? patient.title + ' ' : ''}${patient.name} ${patient.surname}`,
      },
      diagnosis: patient.diagnosis,
      prescriptions,
      icdCodes: patient.icdCodes
    };
  }

  async downloadPdf(content: any, allowSigning: boolean): Promise<Uint8Array> {
    if (isPlatformBrowser(this.platformId)) {
      const [jsPDFModule, autoTableModule, QRCode] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
        import('qrcode')
      ]);

      const prescription = content;
      const patient = content.patientDetails;
      const physician = content.physicianDetails;
      const group = content.groupDetails;
      const medication = prescription.medication;

      const { jsPDF } = jsPDFModule;
      const currentDateTime = formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss', 'en-US');

      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      // Adjusted page width for margins (if needed)
      const pageWidth = doc.internal.pageSize.getWidth() + 30;

      // Load images
      let logoImage = await this.getBase64ImageFromURL(prescription.headerImageUrl);
      if (!logoImage) {
        // The letterhead snapshot stored on the prescription may point at a blob that was
        // removed when the group changed its letterhead. Fall back to the group's current
        // letterhead, and ultimately to the default asset (getBase64ImageFromURL returns the
        // bundled logo when given an empty url). On the public verify page there is no logged-in
        // group, so this simply resolves to the default asset.
        logoImage = await this.getBase64ImageFromURL(this.groupService.group?.headerImageUrl);
      }
      const signatureImage = await this.getBase64ImageFromURL(prescription.signatureImageUrl);
      /* const footerImage = prescription.footerImageUrl
        ? await this.getBase64ImageFromURL(prescription.footerImageUrl)
        : null; */
      const footerImage = null;

      // QR Code Data
      const qrData = `${environment.BASE_URL}/verify-prescription/${prescription.id}`;
      const qrCodeImage = await QRCode.toDataURL(qrData, { scale: 2 });

      // Header function
      const headerFunction = () => {
        return new Promise<void>((resolve) => {
          const logoHeight = 35;
          const pageCenter = doc.internal.pageSize.getWidth() / 2;
          // Always render the title; the logo is drawn only if it loads successfully.
          const drawTitle = () => {
            doc.setFontSize(15);
            doc.setTextColor('#353740');
            doc.setFont('helvetica', 'bold');
            doc.text('Prescription For Medicine', pageCenter, 5 + logoHeight + 10, { align: 'center' });
          };
          // No usable letterhead image — render the title only rather than hanging.
          if (!logoImage) {
            drawTitle();
            resolve();
            return;
          }
          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.width / img.height;
            const logoWidth = logoHeight * aspectRatio;
            const logoX = pageCenter - logoWidth / 2;
            const logoY = 5;
            doc.addImage(img, 'PNG', logoX, logoY, logoWidth, logoHeight);
            drawTitle();
            resolve();
          };
          // If the image fails to load, don't hang the PDF — render the title and continue.
          img.onerror = () => {
            drawTitle();
            resolve();
          };
          img.src = logoImage.indexOf('data:') === -1 ? `data:image/png;base64,${logoImage}` : logoImage;
        });
      };

      // Footer function
      const footerFunction = (pageNumber: number, totalPages: number) => {
        return new Promise<void>((resolve) => {

          /* if (footerImage) {
            const footerImageHeight = 35;
            const pageCenter = doc.internal.pageSize.getWidth() / 2;
            const img = new Image();
            img.onload = () => {
              const aspectRatio = img.width / img.height;
              const footerImageWidth = footerImageHeight * aspectRatio;
              const imgX = pageCenter - footerImageWidth / 2;
              const imgY = doc.internal.pageSize.getHeight() - 45;
              doc.addImage(img, 'PNG', imgX, imgY, footerImageWidth, footerImageHeight);
              doc.setFontSize(8);
              doc.text(
                `Generated by ${physician.title} ${physician.name} ${physician.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`,
                10,
                doc.internal.pageSize.getHeight() - 5
              );
              doc.text(
                `Page ${pageNumber} of ${totalPages}`,
                doc.internal.pageSize.getWidth() - 10,
                doc.internal.pageSize.getHeight() - 5,
                { align: 'right' }
              );
              resolve();
            };
            img.src = footerImage.indexOf('data:') === -1 ? `data:image/png;base64,${footerImage}` : footerImage;
          } else { */
          doc.setFontSize(8);
          doc.text(
            `Generated by ${physician.title} ${physician.name} ${physician.surname} using MedicAI (contact@medicai.co.za) on ${currentDateTime}`,
            10,
            doc.internal.pageSize.getHeight() - 10
          );
          doc.text(
            `Page ${pageNumber} of ${totalPages}`,
            doc.internal.pageSize.getWidth() - 10,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'right' }
          );
          resolve();
          /*  } */
        });
      };

      // Helper function to draw signature and QR code side by side
      const drawSignatureAndQRCodeSideBySide = async (y: number): Promise<number> => {
        // Signature dimensions
        const signatureHeight = 25;
        // Draw signature image if available. signatureImage can be null if the stored
        // signature blob no longer exists — skip drawing rather than throwing/hanging.
        if (prescription.signatureImageUrl && signatureImage) {
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
              const aspectRatio = img.width / img.height;
              const sigWidth = signatureHeight * aspectRatio;
              // Draw signature on the left (using a left margin of 10)
              doc.addImage(img, 'PNG', 10, y, sigWidth, signatureHeight);
              resolve();
            };
            // If the signature image fails to load, continue without it.
            img.onerror = () => resolve();
            img.src = signatureImage.indexOf('data:') === -1
              ? `data:image/png;base64,${signatureImage}`
              : signatureImage;
          });
        }
        // Draw QR code on the right side
        // We'll use the page width to position the QR code with a margin of 10
        const qrCodeWidth = 40;
        const qrCodeHeight = 40;
        const pageActualWidth = doc.internal.pageSize.getWidth();
        const qrX = pageActualWidth - qrCodeWidth - 15;
        const qrY = y - 17;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        // Place the label "Scan to Verify" slightly above the QR code
        doc.text('Scan to Verify', qrX + qrCodeWidth / 2, qrY, { align: 'center' });
        doc.addImage(qrCodeImage, 'PNG', qrX, qrY, qrCodeWidth, qrCodeHeight);
        // Return the vertical space used (the larger of the two)
        return Math.max(signatureHeight, qrCodeHeight);
      };

      // Begin content layout
      let y = 65; // Starting vertical position (adjusted for header)
      let rightY = 65;
      const lineHeight = 5;
      const pageInnerWidth = doc.internal.pageSize.getWidth();
      // Keep left column clear of the right column (date / physician block)
      const leftMaxWidth = pageInnerWidth - 20 - 85;
      const rightColMaxWidth = 90;

      const drawWrappedLeft = (text: string): number => {
        const normalized = String(text).replace(/\r\n/g, '\n').trim();
        if (!normalized) {
          return 0;
        }
        const lines = doc.splitTextToSize(normalized, leftMaxWidth);
        if (!lines.length) {
          return 0;
        }
        doc.text(lines, 10, y);
        return lines.length * lineHeight;
      };

      const drawWrappedRight = (text: string): number => {
        const normalized = String(text).replace(/\r\n/g, '\n').trim();
        if (!normalized) {
          return 0;
        }
        const lines = doc.splitTextToSize(normalized, rightColMaxWidth);
        if (!lines.length) {
          return 0;
        }
        doc.text(lines, pageWidth - 40, rightY, { align: 'right' });
        return lines.length * lineHeight;
      };

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`${patient.name}`, 10, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(`${patient.dob} (${this.ageFromDate.transform(patient.dob)}${patient.gender ? ', ' + patient.gender.substring(0, 1) : ''})`, 10, y);
      if (patient.idNumber) {
        y += 5;
        doc.text(`ID: ${patient.idNumber}`, 10, y);
      } else if (patient.passportNumber) {
        y += 5;
        doc.text(`Passport: ${patient.passportNumber}`, 10, y);
      }
      if (patient.address) {
        y += 5;
        y += drawWrappedLeft(`${patient.address}`);
      }
      if (patient.email) {
        y += 5;
        doc.text(`${patient.email}`, 10, y);
      }
      if (patient.mobileNumber) {
        y += 5;
        doc.text(`${patient.mobileNumber}`, 10, y);
      }
      const aid = medicalAidDisplay(patient);
      if (aid.name) {
        y += 10;
        doc.setFont('helvetica', 'bold');
        doc.text(`Medical Aid:`, 10, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        y += drawWrappedLeft(
          `* ${aid.name}${aid.plan ? ' - ' + aid.plan : ''}${patient.medicalAidNumber ? ' (' + patient.medicalAidNumber + ')' : ''}`
        );
      }
      if (patient.gapCover) {
        y += 10;
        doc.setFont('helvetica', 'bold');
        doc.text(`Gap Cover:`, 10, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        y += drawWrappedLeft(`* ${patient.gapCover}${patient.gapCoverNumber ? ' (' + patient.gapCoverNumber + ')' : ''}`);
      }
      if (patient.diagnosis) {
        y += 10;
        doc.setFont('helvetica', 'bold');
        doc.text('Diagnosis:', 10, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        const icdPart = (patient.icdCodes || []).map((c: any) => c.code).join(', ');
        y += drawWrappedLeft(`* ${patient.diagnosis} (${icdPart})`);
      }
      y += 10;

      doc.setFont('helvetica', 'bold');
      doc.text(`Date: ${prescription.date}`, pageWidth - 40, rightY, { align: 'right' });
      rightY += 5;
      doc.text(`Prescription ID: ${prescription.prescriptionNumber}`, pageWidth - 40, rightY, { align: 'right' });
      rightY += 5;
      doc.text(`${physician.title} ${physician.name} ${physician.surname}`, pageWidth - 40, rightY, { align: 'right' });
      rightY += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(`MP No.: ${physician.mpNumber}`, pageWidth - 40, rightY, { align: 'right' });
      if (group.practiceNumber) {
        rightY += 5;
        doc.text(`Practice No.: ${group.practiceNumber}`, pageWidth - 40, rightY, { align: 'right' });
      }
      if (group.practiceAddress) {
        rightY += 5;
        rightY += drawWrappedRight(`Address: ${group.practiceAddress}`);
      }
      if (group.practiceEmail) {
        rightY += 5;
        doc.text(`${group.practiceEmail}`, pageWidth - 40, rightY, { align: 'right' });
      }
      if (group.practicePhone) {
        rightY += 5;
        doc.text(`${group.practicePhone}`, pageWidth - 40, rightY, { align: 'right' });
      }
      if (y <= rightY) {
        y = Math.floor(rightY + 10);
      }

      // Draw autoTable for medications
      const continuationPages: { pageNumber: number; y: number }[] = [];
      autoTable(doc, {
        startY: y,
        head: [['No.', 'Medication', 'Dosage', 'Frequency', 'Route', 'Duration', 'Instructions', 'Repeat']],
        body: [
          ...medication.map((p: any, index: number) => [
            index + 1,
            p.medication,
            `${p.dose} ${p.unit}`,
            p.frequency,
            p.route,
            p.duration,
            p.instructions,
            p.repeat
          ]),
          ['', '*END OF PRESCRIPTION*', '', '', '', '', '', '']
        ],
        headStyles: {
          fillColor: [64, 64, 64],
          textColor: [255, 255, 255]
        },
        margin: { top: 75, bottom: footerImage ? 55 : 20 },
        didDrawPage: function (data) {
          continuationPages.push({ pageNumber: data.pageNumber, y: data.cursor!.y });
          //console.log('didDrawPage: ', footerImage);
          if (data.cursor!.y > doc.internal.pageSize.getHeight() - (footerImage ? 55 : 20)) {
            doc.addPage();
            y = 75;
          } else {
            y = data.cursor!.y + 10;
          }
        },
      });

      //console.log('before transfer as original: ', footerImage);
      if (y > doc.internal.pageSize.getHeight() - (footerImage ? 55 : 20)) {
        doc.addPage();
        y = 75;
      }

      doc.setFont('helvetica', 'bold');
      doc.text('Transfer as original script to pharmacy.', 10, y);
      if (prescription.confirmSubstitutions) {
        y += 5;
        doc.text('All medicine substitutions must first be confirmed with the prescribing physician.', 10, y);
      }
      y += 10;
      doc.setFont('helvetica', 'normal');

      // Now render signature and QR code side by side if signing is allowed;
      // otherwise, just render the QR code.
      if (allowSigning) {
        const signatureHeight = 25;
        const qrCodeHeight = 40;
        // For side-by-side, the required vertical space is the taller of the two plus a small buffer.
        const requiredHeight = Math.max(signatureHeight, qrCodeHeight) + 10;
        if (y + requiredHeight > doc.internal.pageSize.getHeight() - (footerImage ? 55 : 20)) {
          doc.addPage();
          y = 65;
        }
        const usedHeight = await drawSignatureAndQRCodeSideBySide(y);
        y += usedHeight + 10;
      } else {
        // Only draw the QR code if signing is not allowed
        const qrCodeHeight = 40;
        if (y + qrCodeHeight + 10 > doc.internal.pageSize.getHeight() - (footerImage ? 55 : 20)) {
          doc.addPage();
          y = 65;
        }
        const pageActualWidth = doc.internal.pageSize.getWidth();
        const qrX = pageActualWidth - 40 - 15;
        const qrY = y - 17;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Scan to Verify', qrX + 20, qrY, { align: 'center' });
        doc.addImage(qrCodeImage, 'PNG', qrX, qrY, 40, 40);
        y += qrCodeHeight + 10;
      }

      // Apply header and footer to each page
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await headerFunction();
        await footerFunction(i, totalPages);
      }

      // Add continuation messages for pages where the table split
      for (let i = 0; i < continuationPages.length - 1; i++) {
        const { pageNumber, y: yPosition } = continuationPages[i];
        doc.setPage(pageNumber);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.text('*CONTINUED ON NEXT PAGE...*', 20, yPosition + 10);
      }

      const pdfArrayBuffer = doc.output('arraybuffer');
      return new Uint8Array(pdfArrayBuffer);
    } else {
      return new Uint8Array();
    }
  }



  private async getBase64ImageFromURL(url?: string | null): Promise<string | null> {
    if (!url) {
      url = 'assets/full-logo.jpg';
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } else {
      // The url may point at a letterhead/signature blob that has since been removed
      // (e.g. the group replaced its letterhead). Fetch defensively and return null on
      // any failure so callers can fall back instead of hanging on a broken image.
      try {
        const proxyUrl = `${environment.API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        return data?.base64Image ?? null;
      } catch {
        return null;
      }
    }
  }

}
